import { ReactNode, useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';

import { deepEqual, hashData } from '../01-core/Utilities';
import { AnimatedDetails } from '../01-core/AnimatedDetails';

export interface AccordeonItemProps {
  /** The ID */
  id?: string;
  /** The item title */
  title: string;
  /** Whether the content is in markdown format or not */
  markdown?: boolean;
  /** The item content (can be a function for lazy loading) */
  content: ReactNode | string | (() => ReactNode);
  /** Whether the item is open initially */
  open?: boolean;
  /** Toggle handler */
  onToggle?: (open: boolean) => void;
}

export interface AccordeonProps {
  /** Accordeon items */
  items: AccordeonItemProps[];
  /** Allow only one open item at a time */
  onlyone?: boolean;
  /** Toggle handler */
  onToggle?: (index: number, open: boolean) => void;
}

/** Block rendering multiple collapsible accordeon items, each having a title and structured content */
export const Accordeon = ({
  onlyone,
  items,
  onToggle,
  ...props
}: AccordeonProps) => {
  const _openItems = {} as { [key: number]: boolean };

  // Memoize the hash calculation to avoid expensive recomputation on every render
  const id = useMemo(() => 'accordeon-' + hashData(items) + '-', [items]);

  const updateOpenItems = (i: number, open: boolean) => {
    const update = { ...openItems };
    if (openItems[i] !== open) {
      update[i] = open;
      if (onToggle) {
        onToggle(i, open);
      }
    }

    if (onlyone && open) {
      items.forEach((_, j) => {
        if (i !== j && openItems[j]) {
          update[j] = false;

          if (onToggle) {
            onToggle(j, false);
          }
        }
      });
    }

    if (!deepEqual(update, openItems)) {
      setOpenItems(update);
    }
  };

  items = items.map((item, i) => {
    item = { ...item };
    item.id = item?.id || id + i;
    item.onToggle = (open) => {
      updateOpenItems(i, open);
    };
    _openItems[i] = !!item?.open;
    return item;
  });

  const [openItems, setOpenItems] = useState(_openItems);

  return (
    <div className="accordeon" {...props}>
      {items.map((item, i) => {
        return (
          <AnimatedDetails
            key={i}
            id={item.id}
            open={openItems[i]}
            className="accordeon__item"
            name={item.id}
            summary={item.title}
            onToggle={item.onToggle}
            calculateHeight={true}
            summaryClassName="accordeon__summary"
            contentClassName="accordeon__wrapper"
          >
            <div className="accordeon__content">
              {openItems[i] ? (
                item.markdown ? (
                  <ReactMarkdown>{item.content as string}</ReactMarkdown>
                ) : typeof item.content === 'function' ? (
                  item.content()
                ) : (
                  item.content
                )
              ) : null}
            </div>
          </AnimatedDetails>
        );
      })}
    </div>
  );
};
